import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useBrandsListQuery, useAestheticsListCachedQuery, useProfileReadQuery, useLogoutCreateMutation } from "../store/api.ts";
import { ReactComponent as EvenessLogo } from "./../assets/svgs/eveness-logo.svg";
import { ReactComponent as BoardsMenuIcon } from "./../assets/svgs/boards-menu-icon.svg";
import { ReactComponent as CartMenuIcon } from "./../assets/svgs/cart-menu-icon.svg";
import { ReactComponent as SearchProductsIcon } from "./../assets/svgs/search-products-icon.svg";
import { ReactComponent as HamburgerIcon } from "./../assets/svgs/hamburger-icon.svg";
import PriceSlider, { PRICE_STEPS } from "./PriceSlider";
import FilterDropdown from "./FilterDropdown.js";
import FilterGroup from "./FilterGroup.js";
import AuthModal from "./AuthModal.js";
import RootPersonalizationModal from "./RootPersonalizedModal.js";
import HomeProfilePopupFlow from "../components/HomeProfilePopupFlow.js";
import MarketplaceWelcomeModal from "./MarketplaceWelcomeModal.js";
import { getPendingSurveyToken } from "../utils.js";
import { useSelector } from "react-redux";
import { selectCartItems } from "../store/cartSlice";
import { clearAuthSession } from "../utils/authSession.js";
import toast from "react-hot-toast";

const MAX_PRICE_STEP = PRICE_STEPS[PRICE_STEPS.length - 1];
let hideProAfterAmirRedirectUntilRefresh = false;

const getPriceIndex = (price) => {
  const exactIndex = PRICE_STEPS.indexOf(price);

  if (exactIndex !== -1) return exactIndex;

  return PRICE_STEPS.reduce((closestIndex, currentPrice, currentIndex) => {
    const currentDiff = Math.abs(currentPrice - price);
    const closestDiff = Math.abs(PRICE_STEPS[closestIndex] - price);

    return currentDiff < closestDiff ? currentIndex : closestIndex;
  }, 0);
};

const getPriceSliderValue = (minPrice = 0, maxPrice = MAX_PRICE_STEP) => [
  getPriceIndex(minPrice),
  getPriceIndex(maxPrice),
];

const marketplaceCategoryOptions = [
  { label: "All Categories", value: "All" },
  { label: "Dresses", value: "Dresses" },
  { label: "Tops", value: "Tops" },
  { label: "Pants", value: "Pants" },
  { label: "Outerwear", value: "Outerwear" },
  { label: "Casual", value: "Casual" },
  { label: "Work", value: "Work" },
  { label: "Evening", value: "Evening" },
  { label: "Summer", value: "Summer" },
];

const marketplaceSustainabilityOptions = [
  { label: "Sustainability", value: "" },
  { label: "Eco-Friendly", value: "Eco-Friendly" },
  { label: "Organic", value: "Organic" },
  { label: "Recycled Materials", value: "Recycled Materials" },
  { label: "Vegan", value: "Vegan" },
  { label: "Fair Trade/Ethical", value: "Fair Trade/Ethical" },
];

const filterSizes = ["XS", "S", "M", "L", "XL", "XXL"];
const filterFits = ["Slim", "Regular", "Relaxed", "Oversized"];
const filterOccasions = ["Casual", "Work", "Formal", "Evening"];
const filterColors = ["Black", "White", "Gray", "Navy", "Brown"];

const desktopIconButtonClass =
  "inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[6px] text-[#545454] transition-colors hover:bg-[#F2F2F2] hover:text-[#242424]";

const mobileIconButtonClass =
  "inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[5px] text-[#5B5B5B] transition-colors hover:bg-[#F2F2F2] hover:text-[#242424]";

const compactDropdownWrapperClass = `
  shrink-0

  [&>div]:relative

  [&>div>button]:h-[30px]
  [&>div>button]:whitespace-nowrap
  [&>div>button]:border-0
  [&>div>button]:bg-transparent
  [&>div>button]:px-0
  [&>div>button]:shadow-none

  [&>div>button]:font-spaceMono
  [&>div>button]:text-[9px]
  [&>div>button]:font-normal
  [&>div>button]:uppercase
  [&>div>button]:tracking-[0.11em]
  [&>div>button]:text-[#242424]

  min-[375px]:[&>div>button]:text-[10px]
  min-[375px]:[&>div>button]:tracking-[0.13em]

  [&>div>button:hover]:bg-transparent
`;

const checkoutStepClass = (isActive) => {
  return `inline-flex h-[33px] min-w-[138px] items-center justify-center rounded-[5px] border px-5 text-[14px] font-medium transition ${
    isActive
      ? "border-[#313131] bg-[#313131] text-white"
      : "border-[#313131] bg-white text-[#313131] hover:bg-[#F7F7F8]"
  }`;
};

const NewNavbar = ({
  bottomNav = "none",
  checkoutStep = "email",
  brandName = "",
  overlayOnContent = false,
  mobileOverlayOnContent = false,
  mobileInFlow = false,
  searchText = "",
  onSearchTextChange,
  showAmirCollection = false,
  logoRedirectPath: logoRedirectPathOverride,
  showCollectionLanguageSelector = false,
  collectionLanguage = "en",
  onCollectionLanguageChange,
  selectedCategory = "All",
  onCategoryChange,

  selectedSort = "",
  onSortChange,

  selectedAesthetic = "",
  onAestheticChange,

  selectedSustainability = "",
  onSustainabilityChange,

  draftFilters,
  onDraftFiltersChange,
  onApplyFilters,
  onResetFilters,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedPathname = location.pathname.replace(/\/+$/, "") || "/";
  const defaultLogoRedirectPath = ["/shop","/eftakhari-collection","/amir"].includes(normalizedPathname) ? normalizedPathname : "/marketplace";
  const logoRedirectPath = logoRedirectPathOverride || defaultLogoRedirectPath;

  const cartItems = useSelector(selectCartItems);
  const isCartEmpty = cartItems.length === 0;

  const hasBottomNav = bottomNav !== "none";
  const isMarketplaceBottomNav = bottomNav === "marketplace";
  const isCartBottomNav = bottomNav === "cart";
  const isAccountBottomNav = bottomNav === "account";
  const isCheckoutBottomNav = bottomNav === "checkout";
  const bottomNavBrandName = String(brandName || "").trim();

  const [scrollDirection, setScrollDirection] = useState("top");
  const [forceFullNav, setForceFullNav] = useState(false);
  const [isNavbarHovered, setIsNavbarHovered] = useState(false);
  const forceFullNavRef = useRef(false);
  const ignoreScrollUntilRef = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const filterModalRef = useRef(null);

  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const directionRef = useRef("top");
  const accumulatedDeltaRef = useRef(0);
  const pendingDirectionRef = useRef(null);
  const lastSwitchAtRef = useRef(0);
  const fullNavLockUntilRef = useRef(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");
  const [marketplaceWelcomeModalOpen, setMarketplaceWelcomeModalOpen] = useState(false);
  const [hideProAfterAmirRedirect, setHideProAfterAmirRedirect] = useState(() => hideProAfterAmirRedirectUntilRefresh);
  const handleAmirCollectionRedirect = () => {
    hideProAfterAmirRedirectUntilRefresh = true;
    setHideProAfterAmirRedirect(true);
  };

  const [authTick, setAuthTick] = useState(0);
  const isAuthenticated = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return Boolean(
      localStorage.getItem("authToken"),
    );
  }, [authTick]);
  const [rootPersonalizationOpen, setRootPersonalizationOpen] = useState(false);
  const [homeProfileFlowOpen, setHomeProfileFlowOpen] = useState(false);
  const [logout] = useLogoutCreateMutation();
  const { data: brands = [], isFetching: areBrandsFetching } = useBrandsListQuery();
  const { data: aesthetics = [], isFetching: areAestheticsFetching } = useAestheticsListCachedQuery(undefined,
    {
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );
  const activeBrandSlug = useMemo(() => {
    const match = location.pathname.match(/^\/brands\/([^/]+)/);
    if (!match?.[1]) return "";
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, [location.pathname]);

  const activeBrand = useMemo(
    () => brands.find((brand) => brand.slug === activeBrandSlug),
    [brands, activeBrandSlug],
  );

  const activeCategorySlug = useMemo(() => {
    const match = location.pathname.match(/^\/categories\/([^/]+)/);

    if (!match?.[1]) return "";

    try {
      return decodeURIComponent(match[1])
        .trim()
        .toLowerCase();
    } catch {
      return String(match[1])
        .trim()
        .toLowerCase();
    }
  }, [location.pathname]);

  const isCategoryPage = Boolean(activeCategorySlug);

  const activeCategory = useMemo(
    () => aesthetics.find((aesthetic) =>
          String(aesthetic?.slug || "")
            .trim()
            .toLowerCase() === activeCategorySlug),
    [aesthetics,activeCategorySlug],
  );

  const categoryNavOptions = useMemo(() =>
      aesthetics.map(
        (aesthetic) => ({
          label: aesthetic.name,
          value: aesthetic.slug,
        }),
      ),
    [aesthetics],
  );

  const marketplaceAestheticOptions = useMemo(
    () => [
      {
        label: "Aesthetic",
        value: "",
      },
      ...aesthetics.map(
        (aesthetic) => ({
          label: aesthetic.name,
          value: aesthetic.slug,
        }),
      ),
    ],
    [aesthetics],
  );

  const brandOptions = useMemo(
    () =>
      brands.map((brand) => ({
        label: brand.name,
        value: brand.slug,
      })),
    [brands],
  );

  const handleBrandSelect = (option) => {
    const selectedSlug = String(option?.value || "").trim();
    if (!selectedSlug) return;
    setMobileMenuOpen(false);
    navigate(`/brands/${encodeURIComponent(selectedSlug)}`);
  };

  const handleCategorySelect = (option) => {
    const selectedSlug = String(option?.value || "").trim();
    if (!selectedSlug) return;
    setMobileMenuOpen(false);
    navigate(`/categories/${encodeURIComponent(selectedSlug,)}`);
  };

  const getStoredAuthUser = () => {
    try {
      return JSON.parse(localStorage.getItem("authUser") || "null");
    } catch {
      return null;
    }
  };

  const handleProtectedNavClick = (path, message) => {
    if (!isAuthenticated) {
      toast.error(message);
      return;
    }
    navigate(path);
    setMobileMenuOpen(false);
  };

  const storedAuthUser = getStoredAuthUser();
  const hasPendingSurveyToken = Boolean(
    getPendingSurveyToken(),
  );

  const {
    data: me,
    error: profileError,
    isFetching: isProfileFetching,
    refetch: refetchProfile,
  } = useProfileReadQuery(undefined, {
    skip: !isAuthenticated,
  });

  const profileMissing =
    isAuthenticated &&
    (profileError?.status === 404 ||
      String(profileError?.data?.detail || "")
        .toLowerCase()
        .includes("no profile"));

  const displayUserName =
    me?.first_name ||
    storedAuthUser?.first_name ||
    storedAuthUser?.email?.split("@")?.[0] ||
    "";

  const hasProfile = isAuthenticated && Boolean(me) && !profileMissing;

  const shouldOpenPersonalization =
    isAuthenticated &&
    !isProfileFetching &&
    !hasPendingSurveyToken &&
    !hasProfile &&
    (profileMissing || storedAuthUser?.is_processed === false);

  const handleProfileButtonClick = () => {
    if (shouldOpenPersonalization) {
      setRootPersonalizationOpen(true);
      return;
    }

    if (hasProfile) {
      navigate("/account/profile");
    }
  };

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      localStorage.removeItem("authToken");
      setMobileMenuOpen(false);
      clearAuthSession();
      window.dispatchEvent(new Event("eveness-auth-changed"));
      window.location.href = "/";
    }
  };

  const openLoginModal = () => {
    setMobileMenuOpen(false);
    setAuthModalMode("login");
    setAuthModalOpen(true);
  };

  const openSignupModal = () => {
    setMobileMenuOpen(false);
    setAuthModalMode("signup");
    setAuthModalOpen(true);
  };

  const openProModal = () => {
    setMobileMenuOpen(false);
    setMarketplaceWelcomeModalOpen(true);
  };

  const handleProModalSignIn = () => {
    setMarketplaceWelcomeModalOpen(false);
    setAuthModalMode("login");
    setAuthModalOpen(true);
  };

  const handleUnlockPro = () => {
    setMarketplaceWelcomeModalOpen(false);
    navigate("/checkout");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const eventTarget = event.target;

      const clickedFilterDropdown =
        eventTarget instanceof Element &&
        Boolean(eventTarget.closest("[data-filter-dropdown]"));

      if (
        !clickedFilterDropdown &&
        !mobileMenuRef.current?.contains(eventTarget)
      ) {
        setMobileMenuOpen(false);
      }

      if (
        !clickedFilterDropdown &&
        filterModalOpen &&
        filterModalRef.current &&
        !filterModalRef.current.contains(eventTarget)
      ) {
        setFilterModalOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
        setFilterModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [filterModalOpen]);

  useEffect(() => {
    const handleAuthChanged = () => {
      setAuthTick((prev) => prev + 1);
    };
    const handleLoginCompleted = (event) => {
      setAuthTick((prev) => prev + 1);

      const hasPendingToken = Boolean(
        getPendingSurveyToken(),
      );

      if (
        event?.detail?.shouldShowPersonalization &&
        !hasPendingToken
      ) {
        setRootPersonalizationOpen(true);
        return;
      }
      setRootPersonalizationOpen(false);
    };

    window.addEventListener("eveness-auth-changed", handleAuthChanged);
    window.addEventListener("eveness-login-completed", handleLoginCompleted);

    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
      window.removeEventListener(
        "eveness-login-completed",
        handleLoginCompleted,
      );
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isProfileFetching) {
      return;
    }

    setRootPersonalizationOpen(
      shouldOpenPersonalization,
    );
  }, [
    isAuthenticated,
    isProfileFetching,
    shouldOpenPersonalization,
    authTick,
  ]);

  useEffect(() => {
    if (!hasBottomNav) {
      setScrollDirection("top");
      setForceFullNav(false);
      forceFullNavRef.current = false;
      return;
    }
    const TOP_THRESHOLD = 12;
    const TOP_SAFE_ZONE = 72;
    const TOP_EXIT_THRESHOLD = 28;
    const DELTA_IGNORE = 2;
    const DOWN_SWITCH_THRESHOLD = 22;
    const UP_SWITCH_THRESHOLD = 34;
    const SWITCH_COOLDOWN_MS = 220;

    const commitDirection = (nextDirection) => {
      if (directionRef.current === nextDirection) return;
      directionRef.current = nextDirection;
      pendingDirectionRef.current = null;
      accumulatedDeltaRef.current = 0;
      lastSwitchAtRef.current = Date.now();
      setScrollDirection(nextDirection);
      ignoreScrollUntilRef.current = Date.now() + SWITCH_COOLDOWN_MS;
    };

    const resetPendingScroll = () => {
      pendingDirectionRef.current = null;
      accumulatedDeltaRef.current = 0;
    };

    const updateScrollDirection = () => {
      const currentY = window.scrollY;
      const now = Date.now();
      const rawDiff = currentY - lastScrollY.current;
      const absDiff = Math.abs(rawDiff);
      if (currentY <= TOP_THRESHOLD) {
        forceFullNavRef.current = false;
        setForceFullNav(false);

        directionRef.current = "top";
        resetPendingScroll();

        setScrollDirection("top");

        lastScrollY.current = currentY;
        ticking.current = false;
        return;
      }

      if (now < fullNavLockUntilRef.current) {
        lastScrollY.current = currentY;
        ticking.current = false;
        return;
      }

      if (now < ignoreScrollUntilRef.current) {
        lastScrollY.current = currentY;
        ticking.current = false;
        return;
      }

      if (absDiff <= DELTA_IGNORE) {
        lastScrollY.current = currentY;
        ticking.current = false;
        return;
      }

      if (forceFullNavRef.current && absDiff >= 12) {
        forceFullNavRef.current = false;
        setForceFullNav(false);
      }

      const nextDirection = rawDiff > 0 ? "down" : "up";
      const activeDirection = directionRef.current;

      if (activeDirection === "top") {
        if (currentY < TOP_SAFE_ZONE) {
          forceFullNavRef.current = false;
          setForceFullNav(false);

          directionRef.current = "top";
          resetPendingScroll();
          setScrollDirection("top");

          lastScrollY.current = currentY;
          ticking.current = false;
          return;
        }
        if (pendingDirectionRef.current !== nextDirection) {
          pendingDirectionRef.current = nextDirection;
          accumulatedDeltaRef.current = absDiff;
        } else {
          accumulatedDeltaRef.current += absDiff;
        }

        const requiredThreshold =
          nextDirection === "up" ? UP_SWITCH_THRESHOLD : TOP_EXIT_THRESHOLD;

        if (accumulatedDeltaRef.current >= requiredThreshold) {
          commitDirection(nextDirection);
        }

        lastScrollY.current = currentY;
        ticking.current = false;
        return;
      }
      if (nextDirection === activeDirection) {
        resetPendingScroll();
        lastScrollY.current = currentY;
        ticking.current = false;
        return;
      }

      const requiredThreshold =
        nextDirection === "up" ? UP_SWITCH_THRESHOLD : DOWN_SWITCH_THRESHOLD;

      if (pendingDirectionRef.current !== nextDirection) {
        pendingDirectionRef.current = nextDirection;
        accumulatedDeltaRef.current = absDiff;
      } else {
        accumulatedDeltaRef.current += absDiff;
      }

      if (accumulatedDeltaRef.current >= requiredThreshold) {
        commitDirection(nextDirection);
      }

      lastScrollY.current = currentY;
      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollDirection);
        ticking.current = true;
      }
    };

    lastScrollY.current = window.scrollY;
    if (window.scrollY <= TOP_THRESHOLD) {
      directionRef.current = "top";
      setScrollDirection("top");
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasBottomNav]);

  const showTopNav = !hasBottomNav
    ? true
    : isNavbarHovered ||
      forceFullNav ||
      scrollDirection === "top" ||
      scrollDirection === "up";

  const showBottomNav =
    hasBottomNav &&
    (isNavbarHovered ||
      forceFullNav ||
      scrollDirection === "top" ||
      scrollDirection === "down");

  useEffect(() => {
    if (!showBottomNav && filterModalOpen) {
      setFilterModalOpen(false);
    }
  }, [showBottomNav, filterModalOpen]);

  const selectedFilterCount = draftFilters
    ? [
        draftFilters.size,
        draftFilters.fit,
        draftFilters.occasion,
        draftFilters.color,
        draftFilters.minPrice !== null && draftFilters.minPrice !== 0
          ? "minPrice"
          : "",
        draftFilters.maxPrice !== null &&
        draftFilters.maxPrice !== MAX_PRICE_STEP
          ? "maxPrice"
          : "",
      ].filter(Boolean).length
    : 0;

  const cartStepClass = (path) => {
    const isActive = location.pathname === path;

    return `inline-flex h-[33px] min-w-[138px] items-center justify-center rounded-[5px] border px-5 text-[14px] font-medium transition ${
      isActive
        ? "border-[#313131] bg-[#313131] text-white"
        : "border-[#313131] bg-white text-[#313131] hover:bg-[#F7F7F8]"
    }`;
  };

  const accountStepClass = (path) => {
    const isActive = location.pathname === path;

    return `inline-flex h-[33px] min-w-[138px] items-center justify-center rounded-[5px] border px-5 text-[14px] font-medium transition ${
      isActive
        ? "border-[#313131] bg-[#313131] text-white"
        : "border-[#313131] bg-white text-[#313131] hover:bg-[#F7F7F8]"
    }`;
  };

  const isActivePath = (paths) => {
    return paths.some((path) => {
      if (path === "/") return location.pathname === "/";
      return (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    });
  };

  const topTextLinkClass = (paths) => {
    const isActive = isActivePath(paths);

    return `
    whitespace-nowrap font-spaceMono text-[10px] font-normal uppercase
    tracking-[12%] transition-colors
    ${
      isActive
        ? isAuthenticated
          ? "text-[#963336]"
          : "text-[#F26633]"
        : "text-[#000000B2] hover:opacity-[80%]"
    }
  `;
  };

  const handleHomeProfileComplete = async () => {
    setHomeProfileFlowOpen(false);
    setRootPersonalizationOpen(false);

    if (isAuthenticated) {
      await refetchProfile?.();
    }

    setAuthTick((prev) => prev + 1);
    window.dispatchEvent(new Event("eveness-auth-changed"));
  };

  return (
    <>
      <header
        className={`top-0 z-[100] w-full bg-transparent px-[10px] pt-[16px] lg:px-5 lg:pt-[18px] ${
          mobileInFlow && overlayOnContent
          ? "sticky lg:fixed lg:left-0 lg:right-0"
          : mobileOverlayOnContent
            ? "fixed left-0 right-0 pt-0 lg:sticky lg:left-auto lg:right-auto"
            : overlayOnContent
              ? "fixed left-0 right-0"
              : "sticky"
        }`}
      >
        <div className="mx-auto flex w-full justify-center">
          <div
            ref={filterModalRef}
            onMouseEnter={() => {
              if (hasBottomNav) {
                setIsNavbarHovered(true);
              }
            }}
            onMouseLeave={() => {
              setIsNavbarHovered(false);
            }}
            className="w-full max-w-[1104px] overflow-visible rounded-[7px] bg-white
            shadow-[0_7px_26px_rgba(0,0,0,0.13)] mt-[10px]
          "
          >
            {" "}
            <div className="flex items-start overflow-visible">
              {" "}
              <div className="min-w-0 flex-1 overflow-visible">
                {/* Top navbar */}
                <div
                  className={`relative z-[80] transition-all duration-300 ease-in-out ${
                    showTopNav
                      ? "max-h-[95px] translate-y-0 opacity-100"
                      : "max-h-0 -translate-y-2 opacity-0 overflow-hidden"
                  }`}
                >
                  <div
                    className={`
                    relative grid min-h-[62px]
                    grid-cols-[auto_minmax(0,1fr)_auto]
                    items-center rounded-[8px] px-[8px] py-[6px]
                    lg:flex lg:justify-between lg:px-[6px] lg:pr-[12px]
                    ${
                      showTopNav && showBottomNav
                        ? "rounded-b-none border-b border-[#ECECEF]"
                        : ""
                    }
                  `}
                  >
                    {/* Left side */}
                    <div className="flex min-w-0 items-center">
                      <Link
                        to={logoRedirectPath}
                        aria-label="Go to home"
                        className="
                          flex h-[52px] w-[50px] shrink-0 items-center justify-center
                          rounded-[5px]
                          bg-[linear-gradient(145deg,#F5D6D1_0%,#D97878_56%,#AE3035_100%)]
                          shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]
                        "
                      >
                        <EvenessLogo className="h-[18px] w-[19px] lg:h-[19px] lg:w-[21px]" />
                      </Link>

                    {showAmirCollection ? (
                    <div className="ml-[6px] flex min-w-0 items-center gap-[4px] sm:ml-[12px] sm:gap-[8px] lg:ml-[20px]">
                      <Link
                        to="/"
                        onClick={handleAmirCollectionRedirect}
                        className="inline-flex min-w-[82px] shrink-0
                          items-center justify-center whitespace-nowrap
                          rounded-[5px] bg-[#FFD6D6] px-3
                          font-spaceMono text-[9px] font-normal uppercase
                          tracking-[0.18em] text-[#313131]
                          transition-opacity hover:opacity-90
                          sm:min-w-[130px] h-[27px] sm:h-[36px]
                          sm:px-5 sm:text-[10px]
                        "
                      >
                        Amir Collection
                      </Link>
                        {showCollectionLanguageSelector && (
                          <button
                            type="button"
                            onClick={onCollectionLanguageChange}
                            aria-label={`Switch language to ${
                              collectionLanguage === "en" ? "German" : "English"
                            }`}
                            className="
                          inline-flex min-w-[40px] shrink-0
                          items-center justify-center whitespace-nowrap
                          rounded-[5px] bg-[#E1E1E1] px-3
                          font-spaceMono text-[9px] font-normal uppercase
                          tracking-[0.18em] text-[#313131]
                          transition-opacity hover:opacity-90
                          sm:min-w-[130px] h-[27px] sm:h-[36px]
                          sm:px-5 sm:text-[10px]
                            "
                          >
                            <span className="hidden lg:inline">Language:&nbsp;</span>
                            <span>{collectionLanguage.toUpperCase()}</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <nav className="ml-[20px] hidden min-w-0 items-center gap-[28px] lg:flex">
                        <Link
                          to="/marketplace"
                          className={topTextLinkClass([
                            "/marketplace",
                            "/marketplace-search-products",
                          ])}
                        >
                          Marketplace
                        </Link>

                        <Link
                          to="/shop"
                          className={topTextLinkClass([
                            "/shop",
                            "/shop/products",
                          ])}
                        >
                          Collection
                        </Link>

                        {!isAuthenticated && (
                          <Link
                            to="/blog"
                            className={topTextLinkClass(["/blog"])}
                          >
                            Blogs
                          </Link>
                        )}
                      <div className={`${compactDropdownWrapperClass}
                          [&>div>button]:h-auto
                          [&>div>button]:text-[10px]
                          mb-[6px]`}>
                      <FilterDropdown
                        label={
                          areBrandsFetching
                            ? "BRANDS..."
                            : activeBrand?.name || "BRANDS"
                        }
                        options={brandOptions}
                        onSelect={handleBrandSelect}
                        searchable
                        searchPlaceholder="Search brands..."
                        emptyMessage="No brands found"
                      />
                      </div>
                      <div
                      className={`${compactDropdownWrapperClass}
                        [&>div>button]:h-auto
                        [&>div>button]:text-[10px]
                        mb-[6px]`}
                    >
                      <FilterDropdown
                        label={
                          areAestheticsFetching
                            ? "CATEGORIES..."
                            : activeCategory?.name ||
                              "CATEGORIES"
                        }
                        options={categoryNavOptions}
                        onSelect={handleCategorySelect}
                        searchable
                        searchPlaceholder="Search categories..."
                        emptyMessage="No categories found"
                      />
                      </div>
                      </nav>
                  )}
                    </div>
                {!showAmirCollection && !hideProAfterAmirRedirect && (
                  <>
                    {/* Mobile centered Pro action */}
                    <button
                      type="button"
                      onClick={openProModal}
                      className="mx-auto flex min-w-0 items-center justify-center gap-[7px]
                        whitespace-nowrap px-1
                        font-spaceMono text-[9px] font-normal uppercase
                        tracking-[0.04em] text-[#FF6F50]
                        transition-opacity hover:opacity-70
                        lg:hidden">
                      <span className="truncate">Upgrade To Pro</span>
                    </button>

                    {/* Desktop centered Pro action */}
                    <button
                      type="button"
                      onClick={openProModal}
                      className={`hidden shrink-0 items-center justify-center gap-[10px]
                        font-helvena text-[10px] font-medium tracking-[0.03em]
                        transition-opacity hover:opacity-70
                        lg:mx-4 lg:flex whitespace-nowrap
                        ${isAuthenticated ? "text-[#E3939A]" : "text-[#F06C55]"}
                      `}>
                      <span className={isAuthenticated ? "uppercase" : ""}>
                        {isAuthenticated ? (
                          <span>Upgrade To Pro</span>
                        ) : (
                          <span className="text-[#F26633CC] font-bold font-spaceMono">
                            Unlock Pro
                          </span>
                        )}
                      </span>
                    </button>
                  </>
                )}
                    {/* Desktop right side */}
                    <nav className="hidden shrink-0 items-center gap-[6px] lg:flex">
                      {isAuthenticated ? (
                        <Link
                          to="/board"
                          aria-label="Boards"
                          title="Boards"
                          className={`${desktopIconButtonClass} ${
                            isActivePath(["/board"])
                              ? "bg-[#F2F2F2] text-[#242424]"
                              : ""
                          }`}
                        >
                          <BoardsMenuIcon />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={openLoginModal}
                          aria-label="Sign in to view boards"
                          title="Boards"
                          className={desktopIconButtonClass}
                        >
                          <BoardsMenuIcon />
                        </button>
                      )}

                      <Link
                        to="/cart"
                        aria-label="Cart"
                        title="Cart"
                        className={`${desktopIconButtonClass} ${
                          isActivePath([
                            "/cart",
                            "/shipping-address",
                            "/order-information",
                          ])
                            ? "bg-[#F2F2F2] text-[#242424]"
                            : ""
                        }`}
                      >
                        <CartMenuIcon />
                      </Link>

                      {isAuthenticated ? (
                        <button
                          type="button"
                          onClick={handleProfileButtonClick}
                          className={`ml-[4px] inline-flex h-[34px] max-w-[190px] items-center gap-[9px]
                            rounded-[5px] bg-[#F0F0F1] px-[10px]
                            font-spaceMono text-[10px] tracking-[0.03em] text-[#616161]
                            transition-colors hover:bg-[#E8E8EA]
                            ${
                              isActivePath(["/account", "/account/profile"])
                                ? "ring-1 ring-[#CFCFD2]"
                                : ""
                            }`}>
                          <span
                            className="h-[20px] w-[20px] shrink-0 rounded-full
                              bg-[linear-gradient(145deg,#F4C9C8_0%,#D96D70_52%,#AC2D31_100%)]
                            "
                          />

                          <span className="truncate">
                            {displayUserName || "Profile"}
                          </span>
                        </button>
                      ) : (
                        <div className="ml-[4px] flex items-center gap-[6px]">
                          <button
                            type="button"
                            onClick={openLoginModal}
                            className="inline-flex h-[34px] items-center justify-center
                              rounded-[5px] bg-[#00000014] px-[10px]
                              font-spaceMono text-[10px] tracking-[10%]
                              text-[#616161] transition-colors hover:bg-[#E5E5E7]
                              ">
                            Log in
                          </button>

                          <button
                            type="button"
                            onClick={openSignupModal}
                            className="
            inline-flex h-[34px] items-center justify-center
            rounded-[5px] bg-[#00000014] px-[10px]
            font-spaceMono text-[10px] tracking-[10%]
            text-[#616161] transition-colors hover:bg-[#E5E5E7]
          "
                          >
                            Sign Up
                          </button>
                        </div>
                      )}
                    </nav>

                    {/* Mobile right-side icons */}
                    <nav
                      ref={mobileMenuRef}
                      className="relative col-start-3 flex shrink-0 items-center justify-self-end gap-[4px] min-[375px]:gap-[7px] lg:hidden"
                    >
                      {isAuthenticated ? (
                        <Link
                          to="/board"
                          aria-label="Boards"
                          title="Boards"
                          className={`${mobileIconButtonClass} ${
                            isActivePath(["/board"])
                              ? "bg-[#F2F2F2] text-[#242424]"
                              : ""
                          }`}
                        >
                          <BoardsMenuIcon />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={openLoginModal}
                          aria-label="Sign in to view boards"
                          title="Boards"
                          className={mobileIconButtonClass}
                        >
                          <BoardsMenuIcon />
                        </button>
                      )}

                      <Link
                        to="/cart"
                        aria-label="Cart"
                        title="Cart"
                        className={`${mobileIconButtonClass} ${
                          isActivePath([
                            "/cart",
                            "/shipping-address",
                            "/order-information",
                          ])                            
                            ? "bg-[#F2F2F2] text-[#242424]"
                            : ""
                        }`}
                      >
                        <CartMenuIcon />
                      </Link>

                      <button
                        type="button"
                        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={mobileMenuOpen}
                        onClick={(event) => {
                          event.stopPropagation();
                          setMobileMenuOpen((prev) => !prev);
                        }}
                        className={mobileIconButtonClass}
                      >
                        <HamburgerIcon />
                      </button>

                      {mobileMenuOpen && (
                        <div
                          className="
          absolute right-0 top-[calc(100%+12px)] z-[999]
          w-[220px] rounded-[10px]
          border border-[#E3E3E6] bg-white p-2
          shadow-[0_12px_35px_rgba(0,0,0,0.16)]
        "
                        >
                      {!showAmirCollection && (
                        <>
                          <Link
                            to="/marketplace"
                            onClick={() => setMobileMenuOpen(false)}
                            className="
            block rounded-[7px] px-4 py-3
            font-helvena text-[14px] font-medium text-[#313131]
            hover:bg-[#F5F5F6]
          "
                          >
                            Marketplace
                          </Link>

                          <Link
                            to="/shop"
                            onClick={() => setMobileMenuOpen(false)}
                            className="
            block rounded-[7px] px-4 py-3
            font-helvena text-[14px] font-medium text-[#313131]
            hover:bg-[#F5F5F6]
          "
                          >
                            Collection
                          </Link>

                          {!isAuthenticated && (
                            <Link
                              to="/blog"
                              onClick={() => setMobileMenuOpen(false)}
                              className="
              block rounded-[7px] px-4 py-3
              font-helvena text-[14px] font-medium text-[#313131]
              hover:bg-[#F5F5F6]
            "
                            >
                              Blogs
                            </Link>
                          )}
                          <div className="px-4 py-3">
                            <FilterDropdown
                              label={
                                areBrandsFetching
                                  ? "Brands..."
                                  : activeBrand?.name || "Brands"
                              }
                              options={brandOptions}
                              onSelect={handleBrandSelect}
                              searchable
                              searchPlaceholder="Search brands..."
                              emptyMessage="No brands found"
                            />
                          </div>
                          <div className="px-4 py-3">
                            <FilterDropdown
                              label={
                                areAestheticsFetching
                                  ? "Categories..."
                                  : activeCategory?.name ||
                                    "Categories"
                              }
                              options={categoryNavOptions}
                              onSelect={handleCategorySelect}
                              searchable
                              searchPlaceholder="Search categories..."
                              emptyMessage="No categories found"
                            />
                            </div>
                          </>
                          )}
                          {isAuthenticated ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setMobileMenuOpen(false);
                                  handleProfileButtonClick();
                                }}
                                className="
                block w-full rounded-[7px] px-4 py-3 text-left
                font-helvena text-[14px] font-medium text-[#313131]
                hover:bg-[#F5F5F6]
              "
                              >
                                Profile
                              </button>

                              <button
                                type="button"
                                onClick={handleLogout}
                                className="
                block w-full rounded-[7px] px-4 py-3 text-left
                font-helvena text-[14px] font-medium text-[#D81938]
                hover:bg-[#FFF1F1]
              "
                              >
                                Log out
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={openLoginModal}
                                className="
                block w-full rounded-[7px] px-4 py-3 text-left
                font-helvena text-[14px] font-medium text-[#313131]
                hover:bg-[#F5F5F6]
              "
                              >
                                Log in
                              </button>

                              <button
                                type="button"
                                onClick={openSignupModal}
                                className="
                block w-full rounded-[7px] px-4 py-3 text-left
                font-helvena text-[14px] font-medium text-[#313131]
                hover:bg-[#F5F5F6]
              "
                              >
                                Sign up
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </nav>
                  </div>
                </div>
                {isAccountBottomNav && (
                  <div className="relative z-[10] px-[10px] pb-[5px] pt-[4px]">
                    <nav className="flex flex-wrap items-center justify-center gap-3">
                      <Link
                        to="/account/profile"
                        className={accountStepClass("/account/profile")}
                      >
                        Profile Styling
                      </Link>

                      <Link
                        to="/account"
                        className={accountStepClass("/account")}
                      >
                        Account
                      </Link>

                      <button
                        type="button"
                        onClick={() =>
                          handleProtectedNavClick(
                            "/account/orders",
                            "Please sign in to view My Orders.",
                          )
                        }
                        aria-disabled={!isAuthenticated}
                        title={
                          !isAuthenticated
                            ? "Sign in to view My Orders"
                            : undefined
                        }
                        className={[
                            accountStepClass("/account/orders"),   
                            !isAuthenticated
                            ? "cursor-not-allowed opacity-40"
                            : "",                            
                        ].join(" ")}
                      >
                        My Orders
                      </button>
                    </nav>
                  </div>
                )}
                {isCheckoutBottomNav && (
                  <div className="relative z-[10] px-[10px] pb-[10px] pt-[4px]">
                    <nav className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (checkoutStep !== "email") {
                            navigate("/cart");
                          }
                        }}
                        className={checkoutStepClass(checkoutStep === "email")}
                      >
                        Order Confirmation
                      </button>
                      <div
                        className={checkoutStepClass(checkoutStep === "shipping")}
                      >
                        Shipping Information
                      </div>
                      <div
                        className={checkoutStepClass(checkoutStep === "payment")}
                      >
                        Payments
                      </div>
                    </nav>
                  </div>
                )}
                {isCartBottomNav && (
                  <div className="relative z-[10] px-[10px] pb-[10px] pt-[4px]">
                    <nav className="flex flex-wrap items-center justify-center gap-3">
                      <Link to="/cart" className={cartStepClass("/cart")}>
                        Cart Summary
                      </Link>

                      {isCartEmpty ? (
                        <button
                          type="button"
                          disabled
                          title="Add items to cart before adding shipping address"
                          className="inline-flex h-[34px] min-w-[138px] cursor-not-allowed items-center justify-center rounded-[4px] border border-[#C9C9C9] bg-[#EFEFF1] px-5 text-[14px] font-medium text-[#9A9AA3]"
                        >
                          Shipping Address
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            handleProtectedNavClick(
                              "/shipping-address",
                              "Please sign in to view the Shipping Address page.",
                            )
                          }
                          aria-disabled={!isAuthenticated}
                          title={
                            !isAuthenticated
                              ? "Sign in to view Shipping Address"
                              : undefined
                          }
                          className={[
                            cartStepClass("/shipping-address"),
                            !isAuthenticated
                              ? "cursor-not-allowed opacity-40"
                              : "",
                          ].join(" ")}
                        >
                          Shipping Address
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() =>
                          handleProtectedNavClick("/account/orders","Please sign in to view My Orders.",
                          )}
                        aria-disabled={!isAuthenticated}
                        title={
                          !isAuthenticated
                            ? "Sign in to view My Orders"
                            : undefined
                        }
                        className={[
                          cartStepClass("/account/orders"),
                          !isAuthenticated
                            ? "cursor-not-allowed opacity-40"
                            : "",
                        ].join(" ")}
                      >
                        My Orders
                      </button>
                    </nav>
                  </div>
                )}
                {/* Bottom navbar */}
                {isMarketplaceBottomNav && (
                  <div
                    className={`relative z-[10] overflow-visible transition-all duration-300 ease-in-out ${
                      showBottomNav
                        ? filterModalOpen
                          ? "max-h-[720px] translate-y-0 opacity-100"
                          : "max-h-[62px] translate-y-0 opacity-100"
                        : "max-h-0 translate-y-2 opacity-0 overflow-hidden"
                    }`}
                  >
                    <div
                      className={`
    flex min-h-[46px] items-center gap-[7px]
    px-[8px] py-[7px]

    ${showTopNav ? "rounded-b-[8px] rounded-t-none" : "rounded-[8px]"}

    lg:justify-between lg:gap-3
    lg:rounded-b-[7px] lg:px-[6px]
  `}
                    >
                      {/* Scrollable filters area */}
                      <div
                        className="
      flex min-w-0 flex-1 items-center gap-[8px]
      overflow-x-auto overflow-y-hidden
      [-ms-overflow-style:none]
      [scrollbar-width:none]
      [&::-webkit-scrollbar]:hidden
      lg:gap-[29px] lg:overflow-visible
    "
                      >
                      {bottomNavBrandName ? (
                        <div
                          title={bottomNavBrandName}
                          className="
                            inline-flex h-[27px] max-w-[220px] shrink-0
                            items-center justify-center rounded-[5px]
                            bg-[#FFD6D6] px-[16px]
                            font-spaceMono text-[10px] font-normal
                            tracking-[0.22em] text-[#313131]
                            sm:max-w-[280px] sm:text-[11px]
                          "
                        >
                          <span className="truncate">
                            {bottomNavBrandName}
                          </span>
                        </div>
                      ) : null}
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            ignoreScrollUntilRef.current = Date.now() + 350;

                            if (!showBottomNav) {
                              directionRef.current = "down";
                              setScrollDirection("down");
                            }

                            setFilterModalOpen((prev) => !prev);
                          }}
                          aria-expanded={filterModalOpen}
                          className={`
        relative inline-flex h-[27px] shrink-0
        items-center justify-center gap-[8px]
        rounded-[5px] bg-[#EBEBEB] px-[12px]

        font-spaceMono text-[10px] font-normal uppercase text-[#333333]
        transition-colors hover:bg-[#E3E3E5]

        ${filterModalOpen ? "ring-1 ring-inset ring-[#D0D0D3]" : ""}
      `}
                        >
                          <span className="whitespace-nowrap text-[10px] pt-[2px]">
                            ▽ ALL FILTERS
                          </span>

                          {selectedFilterCount > 0 && (
                            <span
                              className="
            inline-flex h-[17px] min-w-[17px]
            items-center justify-center rounded-full
            bg-[#D96769] px-[4px]
            text-[8px] font-semibold tracking-normal text-white
          "
                            >
                              {selectedFilterCount}
                            </span>
                          )}
                        </button>

                        {/* Existing filtering options remain unchanged */}
                        <div className="flex shrink-0 items-center gap-[18px] lg:gap-[29px]">
                          <div className={compactDropdownWrapperClass}>
                            <FilterDropdown
                              label={
                                selectedCategory && selectedCategory !== "All"
                                  ? selectedCategory
                                  : "CATEGORIES"
                              }
                              options={marketplaceCategoryOptions}
                              onSelect={(option) => {
                                onCategoryChange?.(option.value);
                              }}
                            />
                          </div>

                        {!isCategoryPage && (
                          <div className={compactDropdownWrapperClass}>
                            <FilterDropdown
                              label={
                                marketplaceAestheticOptions.find(
                                  (option) =>
                                    option.value === selectedAesthetic,
                                )?.label || "Aesthetic"
                              }
                              options={marketplaceAestheticOptions}
                              onSelect={(option) => {
                                onAestheticChange?.(option.value);
                              }}
                            />
                          </div>
                        )}

                          <div className={compactDropdownWrapperClass}>
                            <FilterDropdown
                              label={
                                marketplaceSustainabilityOptions.find(
                                  (option) =>
                                    option.value === selectedSustainability,
                                )?.label || "Sustainability"
                              }
                              options={marketplaceSustainabilityOptions}
                              onSelect={(option) => {
                                onSustainabilityChange?.(option.value);
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Search remains fixed on the right */}
                      <div
                        className="
      flex h-[27px] w-[84px] shrink-0 items-center
      rounded-[5px] px-[12px]
      transition-[width] duration-200
      focus-within:w-[125px]

      min-[400px]:w-[96px]
      min-[400px]:focus-within:w-[145px]
      lg:w-[218px] border border-[#808080]
      bg-white lg:px-[10px] lg:focus-within:w-[218px]
    "
                      >
                        <SearchProductsIcon className="mr-[7px] mb-[2px]" />
                        <input
                          type="text"
                          value={searchText}
                          onChange={(event) => {
                            onSearchTextChange?.(event.target.value);
                          }}
                          placeholder="Search 100K+ pieces"
                          aria-label="Search product"
                          className="
        min-w-0 flex-1 border-0 bg-transparent p-0
        font-spaceMono text-[9px] font-normal text-[#444444]
        outline-none placeholder:text-[#00000080]
        min-[375px]:text-[10px]
      "
                        />

                        <span
                          className="
        ml-[7px] hidden h-[15px] shrink-0
        items-center justify-center rounded-[4px]
        bg-[#0000001F] px-[6px]
        font-spaceMono text-[9px] text-[#00000099]
        lg:inline-flex
      "
                        >
                          ⌘K
                        </span>
                      </div>
                    </div>{" "}
                    {filterModalOpen && draftFilters && (
                      <div className="border-t border-[#D8D8D8] bg-[#E9E9E9] px-3 pb-3 pt-5 md:px-6 md:pb-4">
                        <div className="max-h-[520px] overflow-y-auto pr-1">
                          <div className="grid gap-x-10 gap-y-10 md:grid-cols-2 xl:grid-cols-3">
                            <FilterGroup
                              title="Size"
                              items={filterSizes}
                              selectedValue={draftFilters.size}
                              onSelect={(value) =>
                                onDraftFiltersChange?.((prev) => ({
                                  ...prev,
                                  size: value,
                                }))
                              }
                            />
                            <FilterGroup
                              title="Fit"
                              items={filterFits}
                              selectedValue={draftFilters.fit}
                              onSelect={(value) =>
                                onDraftFiltersChange?.((prev) => ({
                                  ...prev,
                                  fit: value,
                                }))
                              }
                            />
                            <FilterGroup
                              title="Occasion"
                              items={filterOccasions}
                              selectedValue={draftFilters.occasion}
                              onSelect={(value) =>
                                onDraftFiltersChange?.((prev) => ({
                                  ...prev,
                                  occasion: value,
                                }))
                              }
                            />
                            <FilterGroup
                              title="Color"
                              items={filterColors}
                              selectedValue={draftFilters.color}
                              onSelect={(value) =>
                                onDraftFiltersChange?.((prev) => ({
                                  ...prev,
                                  color: value,
                                }))
                              }
                            />
                            <div className="min-w-0">
                              <h3 className="mb-2 text-[14px] font-semibold text-[#111827] font-inter">
                                Price
                              </h3>

                              <div className="w-full">
                                <PriceSlider
                                  value={getPriceSliderValue(
                                    draftFilters.minPrice ?? 0,
                                    draftFilters.maxPrice ?? MAX_PRICE_STEP,
                                  )}
                                  onChange={([minIdx, maxIdx]) => {
                                    onDraftFiltersChange?.((prev) => ({
                                      ...prev,
                                      minPrice: PRICE_STEPS[minIdx],
                                      maxPrice: PRICE_STEPS[maxIdx],
                                    }));
                                  }}
                                />
                              </div>

                              <div className="mt-4 flex items-center gap-3 text-[12px] text-[#5D6775]">
                                <span className="rounded-[5px] bg-[#F5F5F7] px-3 py-2">
                                  ${draftFilters.minPrice ?? 0}{" "}
                                </span>

                                <span>to</span>

                                <span className="rounded-[5px] bg-[#F5F5F7] px-3 py-2">
                                  {(draftFilters.maxPrice ?? MAX_PRICE_STEP) ===
                                  MAX_PRICE_STEP
                                    ? `$${MAX_PRICE_STEP}+`
                                    : `$${draftFilters.maxPrice}`}{" "}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-5 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={onResetFilters}
                            className="inline-flex h-[38px] items-center gap-3 rounded-[5px] bg-white px-4 text-[14px] font-medium text-[#313131] shadow-sm font-helvena"
                          >
                            <span className="flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#416C98] text-[13px] font-semibold text-white">
                              {selectedFilterCount}
                            </span>
                            <span>Filters Selected</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              onApplyFilters?.();
                              setFilterModalOpen(false);
                            }}
                            className="h-[38px] rounded-[5px] bg-[#416C98] px-6 text-[14px] font-semibold text-white font-helvena hover:bg-[#365D85]"
                          >
                            Show Results
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}{" "}
              </div>
            </div>
          </div>
          <RootPersonalizationModal
            isOpen={
              rootPersonalizationOpen &&
              shouldOpenPersonalization
            }
            variant="user"
            onClose={() => setRootPersonalizationOpen(false)}
            onUnlockMatch={() => {
              setRootPersonalizationOpen(false);
              setHomeProfileFlowOpen(true);
            }}
          />
            <HomeProfilePopupFlow
              isOpen={homeProfileFlowOpen}
              mode="restart"
              onClose={() => setHomeProfileFlowOpen(false)}
              onRestartComplete={handleHomeProfileComplete}
              isAuthenticated={isAuthenticated}
          />
          <MarketplaceWelcomeModal
            isOpen={marketplaceWelcomeModalOpen}
            isAuthenticated={isAuthenticated}
            onClose={() => setMarketplaceWelcomeModalOpen(false)}
            onUnlockPro={handleUnlockPro}
            onSignIn={handleProModalSignIn}
          />
          <AuthModal
            isOpen={authModalOpen}
            initialMode={authModalMode}
            onClose={() => setAuthModalOpen(false)}
          />
        </div>
      </header>
    </>
  );
};

export default NewNavbar;
